/**
 * Legal document identity and the operator's identification data.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS FILE IS THE ONLY PLACE THE COMPANY DETAILS APPEAR. Fill in every value
 * marked TODO before going live: Legea 365/2002 art. 5 makes these mandatory for
 * an online service provider, and both legal pages render them from here.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/**
 * Version of the Terms & Conditions the UI currently renders. Submitted with
 * every sign-up and compared server-side against
 * `LegalDocuments.CurrentTermsVersion` (backend/src/EasyPlanner.Common/Legal).
 *
 * **Bump both together, plus `lastUpdated` below, whenever the text changes.**
 * A mismatch makes the server refuse the sign-up and ask the user to re-read the
 * terms — correct for a page left open across a deploy, but it means a partial
 * bump takes signup down. They are two constants because only the client knows
 * which wording it actually showed.
 */
export const TERMS_VERSION = "2026-09-29";

/** Human-readable date shown at the top of both documents. Keep in step with TERMS_VERSION. */
export const LAST_UPDATED = {
  ro: "29 septembrie 2026",
  en: "29 September 2026",
};

/**
 * The date at the top of the Privacy and Cookie policies, which change without
 * the Terms changing. Those two are information notices, not something anyone
 * accepts, so moving this date asks nobody to agree again — unlike
 * TERMS_VERSION, which a sign-up is checked against.
 */
export const PRIVACY_LAST_UPDATED = {
  ro: "27 octombrie 2026",
  en: "27 October 2026",
};

/**
 * How long a visitor's answer to the cookie banner stands — yes or no — before
 * it is asked again. The policy prints it and utils/cookieConsent.js enforces
 * it, so the two cannot disagree.
 */
export const COOKIE_CHOICE_MONTHS = 12;

/**
 * Meta, which receives what the Meta Pixel collects — only from a visitor who
 * accepted marketing cookies.
 *
 * Not a SUBPROCESSORS row, and deliberately: a processor may use data only on
 * our instructions, and Meta uses pixel data for its own purposes too. For the
 * collection and the transmission we are joint controllers with it (CJEU
 * C-40/17, Fashion ID), on the terms of Meta's controller addendum; what Meta
 * does afterwards it does as a controller in its own right. GDPR art. 26(2)
 * wants the essence of that arrangement published, which the Privacy and
 * Cookie policies do from here.
 */
export const META_PIXEL_PARTNER = {
  name: "Meta Platforms Ireland Limited",
  address: "Merrion Road, Dublin 4, D04 X2K5, Irlanda",
  addressEn: "Merrion Road, Dublin 4, D04 X2K5, Ireland",
  /** Meta's own privacy policy, where its processing is described. */
  privacyPolicy: "https://www.facebook.com/privacy/policy",
  /** The joint-controller terms every pixel in the EU runs under. */
  controllerAddendum: "https://www.facebook.com/legal/controller_addendum",
  /** Where a Facebook or Instagram user controls the ads Meta shows them. */
  adPreferences: "https://accountscenter.facebook.com/ad_preferences",
  /** How long Meta's first-party cookies (_fbp, _fbc) live, in days. */
  cookieDays: 90,
};

/**
 * The registered office, in the two halves the footer prints it in. Joined back
 * together for `COMPANY.registeredOffice`, so there is still exactly one copy
 * of the address in the codebase.
 */
export const REGISTERED_OFFICE = {
  street: "Str. Transilvania nr. 30, cam. 1",
  cityLine: "Pitești, jud. Argeș, 110419, România",
};

/**
 * The operator. Every TODO is a legal requirement, not a nicety.
 */
export const COMPANY = {
  /** Denumirea completă, ex. "EasyPlanner Software S.R.L." */
  legalName: "NETSOFT DEVELOPMENT S.R.L.",
  /** Numele comercial al platformei, dacă diferă. */
  brandName: "EasyPlanner",
  /** Sediul social complet: stradă, număr, oraș, județ, cod poștal. */
  registeredOffice: `${REGISTERED_OFFICE.street}, ${REGISTERED_OFFICE.cityLine}`,
  /** Codul unic de înregistrare (CUI/CIF). */
  taxId: "45289155",
  /** Numărul de ordine în registrul comerțului, ex. "J40/1234/2025". */
  tradeRegisterNumber: "J03/2811/2021",
  /**
   * Adresa de e-mail de contact — trebuie monitorizată. Cutia firmei, nu una
   * separată pe domeniul produsului: aceasta este cea care chiar se citește.
   *
   * Este și destinația formularului de contact, care trimite prin API la
   * `Email:ContactAddress` (backend/src/EasyPlanner.API/appsettings*.json).
   * **Cele două trebuie schimbate împreună**: adresa afișată lângă formular și
   * cutia în care ajunge mesajul sunt, pentru vizitator, același lucru — dacă
   * se despart, mesajul ajunge undeva unde nimeni nu se uită, iar el e anunțat
   * că a fost trimis.
   */
  email: "contact@easyplanner.ro",
  /** Telefon de contact. */
  phone: "+40 720 339 694",
  /**
   * Responsabilul cu protecția datelor, dacă a fost desemnat. Nu este obligatoriu
   * pentru orice societate (GDPR art. 37); lasă null dacă nu ai desemnat unul, iar
   * pagina va afișa adresa generală de contact în locul lui.
   */
  dpoEmail: null,
};

/** True while any company field is still a placeholder — used to warn in dev. */
export const COMPANY_DETAILS_INCOMPLETE = Object.values(COMPANY).some(
  (value) => typeof value === "string" && value.startsWith("TODO_"),
);

/**
 * How long technical security logs are kept, in days.
 *
 * GDPR art. 13(2)(a) wants a period, not a range, so this is a number rather
 * than prose — the privacy policy renders it. Thirty days is the shortest span
 * that is still useful for tracing an incident backwards, which is the only
 * reason these logs exist.
 */
export const LOG_RETENTION_DAYS = 30;

/**
 * Everyone who processes personal data on our behalf.
 *
 * Naming them is mandatory under GDPR art. 13(1)(e), not a courtesy, and the
 * list has to be true rather than complete-looking: a processor left off is a
 * disclosure that is wrong, and one invented is worse. Each entry states where
 * the processing happens, because that is what decides whether a transfer
 * outside the EEA needs a safeguard named alongside it.
 *
 * Add a row here when a new service starts touching user data, and both the
 * Privacy Policy and the processor clauses in the Terms pick it up.
 */
export const SUBPROCESSORS = [
  {
    name: "Cloudflare, Inc.",
    role: {
      ro: "rețea de livrare și protecție a traficului",
      en: "content delivery and traffic protection",
    },
    location: { ro: "SUA", en: "USA" },
    // Every request to the site passes through them, so they see connection
    // data even though they store none of the application's own records.
    outsideEea: true,
  },
  {
    name: "Zoho Corporation B.V. (ZeptoMail)",
    role: {
      ro: "livrarea e-mailurilor tranzacționale",
      en: "transactional e-mail delivery",
    },
    location: { ro: "Uniunea Europeană", en: "European Union" },
    outsideEea: false,
  },
  {
    name: "NETOPIA FINANCIAL SERVICES S.A.",
    role: {
      ro: "procesarea plăților cu cardul",
      en: "card payment processing",
    },
    location: { ro: "România", en: "Romania" },
    outsideEea: false,
  },
  {
    name: "OBLIO SOFTWARE S.R.L.",
    role: {
      ro: "emiterea facturilor și transmiterea lor în SPV",
      en: "invoice issuing and SPV submission",
    },
    location: { ro: "România", en: "Romania" },
    outsideEea: false,
  },
];

/**
 * Where a consumer goes when they are not happy and we have not fixed it.
 *
 * The EU's own ODR platform closed on 20 July 2025 under Regulation (EU)
 * 2024/3228, so there is no "SOL" address to link any more — the badge most
 * Romanian shops still show points at a notice of its own closure. What remains
 * is ANPC's SAL procedure and the Commission's list of dispute-resolution
 * bodies, and those are what we link.
 */
export const REDRESS = {
  /** ANPC's own SAL page. Not /ce-este-sal/, which 404s since their redesign. */
  anpcSal: "https://www.anpc.ro/sal",
  /** ANPC's home, for the general complaint route. */
  anpc: "https://anpc.ro",
  /** The Commission's list of ADR bodies, which replaced the ODR platform. */
  euDisputeBodies:
    "https://consumer-redress.ec.europa.eu/dispute-resolution-bodies",
  /**
   * Where a consumer withdraws from the contract, online.
   *
   * Art. 11^1 of OUG 34/2014 wants a mechanism, not an address to write to, so
   * this is a page with a form on it. Kept here rather than inline in the legal
   * text because three documents point at it and a path that drifted in one of
   * them would send somebody to a 404 at the exact moment they are trying to
   * exercise a right.
   */
  withdrawalPath: "/withdrawal",
};

/** The payment processor, for the logo shown at checkout. */
export const PAYMENT_PROCESSOR = {
  name: "NETOPIA Payments",
  url: "https://netopia-payments.com/",
  logo: "/images/netopia-payments.png",

  /**
   * Our merchant id in NETOPIA's logo service, which the footer badge needs.
   *
   * Called a "secret" in their snippet, and it is not one: it travels as a
   * query parameter on an image every visitor loads, so it is public the moment
   * the page renders. It identifies which merchant the badge belongs to — which
   * is the point, since the badge is what they check for when approving the
   * point of sale. Nothing can be authorised with it.
   */
  logoWidgetId: "169104",
};
